import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const slides = [
  {
    badge: "Unlock the Hidden Gold",
    title: "In Your",
    highlight: "Dirt",
    subtitle: "AI-powered land monetization. Retain ownership. Generate income. No selling required.",
    image: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20golden%20Indian%20farmland%20at%20sunrise%20with%20misty%20rolling%20hills%2C%20dramatic%20warm%20light%20breaking%20through%20clouds%2C%20cinematic%20landscape%20photography%2C%20rich%20amber%20and%20deep%20teal%20tones%2C%20highly%20detailed%20atmospheric%20background&width=1800&height=800&seq=hero-slide-01&orientation=landscape&nocache=true",
  },
  {
    badge: "Monetization",
    title: "Build Without",
    highlight: "Selling",
    subtitle: "Joint ventures, lease models & development partnerships",
    image: "https://readdy.ai/api/search-image?query=Modern%20construction%20site%20at%20twilight%20with%20cranes%20and%20illuminated%20building%20frame%20against%20dramatic%20dark%20sky%2C%20warm%20amber%20construction%20lights%2C%20cinematic%20urban%20development%20photography%2C%20deep%20teal%20atmospheric%20tones%2C%20India%20city%20skyline%20in%20background%2C%20highly%20detailed&width=1800&height=800&seq=hero-slide-02&orientation=landscape&nocache=true",
  },
  {
    badge: "Technology",
    title: "Data-Driven",
    highlight: "Decisions",
    subtitle: "AI analysis across zoning, demand & catchment data",
    image: "https://readdy.ai/api/search-image?query=Abstract%20futuristic%20holographic%20data%20visualization%20floating%20over%20dark%20landscape%2C%20glowing%20teal%20and%20amber%20data%20streams%20and%20charts%2C%20AI%20analytics%20concept%2C%20cinematic%20technology%20atmosphere%2C%20dark%20moody%20background%20with%20luminous%20accents%2C%20highly%20detailed&width=1800&height=800&seq=hero-slide-03&orientation=landscape&nocache=true",
  },
  {
    badge: "Growth",
    title: "From Farm to",
    highlight: "Fortune",
    subtitle: "Transform agricultural land into high-yield income streams",
    image: "https://readdy.ai/api/search-image?query=Lush%20green%20Indian%20agricultural%20fields%20transforming%20into%20modern%20solar%20farm%20at%20golden%20hour%2C%20dramatic%20warm%20sunset%20light%2C%20sustainable%20development%20concept%2C%20cinematic%20aerial%20photography%2C%20amber%20and%20teal%20color%20grading%2C%20highly%20detailed%20atmospheric%20landscape&width=1800&height=800&seq=hero-slide-04&orientation=landscape&nocache=true",
  },
];

const heroStats = [
  { value: "₹ 2,400 Cr+", label: "Property value assessed" },
  { value: "12", label: "Use-cases modelled" },
  { value: "48 hrs", label: "AI report turnaround" },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((index: number) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(index);
      setFading(false);
    }, 400);
  }, []);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden mt-[65px]">
      {/* Slides */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={`${s.title} ${s.highlight}`}
            className="w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50/45 md:to-background-50/40" />
        </div>
      ))}

      {/* Content */}
      <div className="absolute inset-0 flex items-center z-10">
        <div className="container mx-auto px-4 lg:px-8 w-full h-full flex items-center">
          <div
            className={`ml-auto max-w-3xl text-right md:pl-16 lg:pl-24 transition-all duration-500 ${
              fading ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">{slide.badge}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92),0_0_8px_rgba(0,0,0,0.72)]">{slide.title}{" "}</span>
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">{slide.highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white max-w-xl ml-auto [text-shadow:0_0_16px_rgba(0,0,0,0.84),0_0_5px_rgba(0,0,0,0.62)]">{slide.subtitle}</p>

            {/* Hero Stats */}
            <dl className="mt-10 grid max-w-xl grid-cols-3 gap-6 ml-auto">
              {heroStats.map((stat) => (
                <div key={stat.label} className="text-right">
                  <dt className="font-display text-2xl md:text-3xl font-bold text-primary-300 [text-shadow:0_0_12px_rgba(0,0,0,0.6)]">{stat.value}</dt>
                  <dd className="mt-1 text-xs text-white/70 [text-shadow:0_0_8px_rgba(0,0,0,0.6)]">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? "bg-primary-500 w-8" : "bg-white/30 w-2 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}